import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useState } from "react"
import { FormattedMessage, FormattedNumber, injectIntl } from "react-intl"
import { useQuery } from "react-query"
import { Link } from "react-router-dom"
import useListingPagination from "../../common/hooks/UseListingPagination"
import { BasicRow } from "../../common/listing/ListingTypes"
import ListingWrapper from "../../common/listing/ListingWrapper"
import { GlobalHelper } from "../../indexImports"
import CreditMemoModalLink from "../../invoice/creditmemomodallink.component"
import { CreditMemoElastic } from "../../state/listing/credit-memo/models/creditMemoElastic"
import { PaymentTransactionElastic } from "../../state/listing/payment-transactions/models"
import { PaymentTransactionsListingService } from "../../state/listing/payment-transactions/service"
import PaymentService from "../../state/payment/PaymentService"

type Params = {
    paymentId: string,
}

const PaymentDetailsTransactionListing = (props: Params) => {
    const paymentId = props.paymentId

    const [creditMemosMap, setCreditMemosMap] = useState<Map<string, CreditMemoElastic>>(new Map())
    const [creditMemoIds, setCreditMemoIds] = useState<string[]>([])

    const {
        paginatedSearch,
        updateTotalItemsCount,
        pagination
    } = useListingPagination({})

    const {
        error,
        isLoading,
        data: paymentTransactionsData,
    } = useQuery(["payment-transactions", paymentId, paginatedSearch], () => {
        return PaymentTransactionsListingService.findPaymentTransactionsByPaymentId({
            ...paginatedSearch,
            paymentId: paymentId,
            keywords: GlobalHelper.formatSearchKeywords(paginatedSearch.keywords)
        })
    }, {
        onSuccess: (data) => {
            const cmIds: Array<string> = []
            data.paymentTransactions.map(txn => {
                cmIds.push(txn.getCreditMemoId())
            })
            setCreditMemoIds(cmIds)
        }
    })

    useQuery(["getCreditMemos", creditMemoIds], () => {
        return PaymentService.getCreditMemosByIds(creditMemoIds)
    }, {
        enabled: !!creditMemoIds,
        onSuccess: (creditMemos) => {
            const cmMap = new Map()
            creditMemos.map(cm => {
                cmMap.set(cm.id, cm)
            })
            setCreditMemosMap(cmMap)
        }
    })

    const calculateTotal = () => {
        let amount = 0
        paymentTransactionsData?.paymentTransactions.map(txn => {
            amount += Number(txn.getAmount())
        })

        return amount
    }

    const totalItemsCount = paymentTransactionsData?.getTotalItemsCount() || 0
    updateTotalItemsCount(totalItemsCount)

    const buildHeader = (): { data: (JSX.Element | string)[]; classNames: string[] } => {
        const rows = [
            <FormattedMessage
                key={"payment.payment_details_transaction_listing.header_document_id_label"}
                id="payment.payment_details_transaction_listing.header_document_id_label"
                defaultMessage="Document ID"/>,
            <FormattedMessage
                key={"payment.payment_details_transaction_listing.header_contact_label"}
                id="payment.payment_details_transaction_listing.header_contact_label"
                defaultMessage="Contact"/>,
            "",
            <FormattedMessage
                key={"payment.payment_details_transaction_listing.header_amount_applied_label"}
                id="payment.payment_details_transaction_listing.header_amount_applied_label"
                defaultMessage="Amount applied"/>
        ]
        const classNames = [" ", " ", " ", "text-right"]
        return {
            classNames: classNames,
            data: rows,
        }
    }

    const buildFooter = (): { data: (JSX.Element | string)[]; classNames: string[] } => {
        let rows = [
            <strong key={"invoice.listing.headers.totals_label"}>
                <FormattedMessage
                    id="invoice.listing.headers.totals_label"
                    defaultMessage="Totals"
                />
            </strong>,
        ]

        rows = rows.concat([
            <></>,
            <></>,
            <div key={"currency_id"} className={"d-flex align-items-center justify-content-end"}>
                <FormattedNumber
                    style="currency"
                    currency={AppContext.getAccount().attributes.currency}
                    value={calculateTotal()}
                />
            </div>,

        ])
        const classNames = [" ", " ", " ", " ", " "]
        return {
            classNames: classNames,
            data: rows,
        }
    }

    const getRows = () => {
        return paymentTransactionsData?.paymentTransactions.map(txn => {
            return mapRow(txn)
        }) || []
    }

    const mapRow = (txn: PaymentTransactionElastic): BasicRow => {
        const creditMemo = creditMemosMap.get(txn.getCreditMemoId())

        return {
            rowId: txn.getId(),
            data: [
                <div key={txn.getId()}>
                    <Link to={"/" + txn.getObjectType() + "/" + txn.getParentId() + "/edit"}>
                        {txn.getId()}
                    </Link>
                </div>,
                <div key={txn.getContactId()}>
                    <Link
                        to={"/contacts/" + txn.getContactId() + "/details"}>
                        {txn.getContactName()}
                    </Link>
                </div>,
                <div key={txn.getCreditMemoId()}>
                    {creditMemo &&
                        <CreditMemoModalLink
                            creditMemoId={creditMemo.id}
                            creditMemoIdentifier={creditMemo.identifier}
                            contactId={creditMemo.contactId}/>
                    }
                </div>,
                <div key={"amount_id"}>
                    {txn.getAmount()}
                </div>,
            ],
            classNames: [" ", " ", "text-right", "text-right pr-4"]
        }
    }

    const standardList = {
        smallLine: true,
        className: "row pl-0 pr-0",
        headers: buildHeader(),
        rows: getRows(),
        pageSize: paginatedSearch.pageSize,
        totalItemsCount: totalItemsCount,
        showSelectionSize: false,
        footer: buildFooter(),
        footerClassName: "bg-secondary text-white"
    }

    return (
        <>
            <div className="my-2">
                <div className="row">
                    <div className="col-12">
                        <h3 className={"mb-0 custom-lead text-secondary"}>
                            <FormattedMessage
                                key={"payment.payment_details_transaction_listing.transactions"}
                                id="payment.payment_details_transaction_listing.transactions"
                                defaultMessage="Transactions"/>
                        </h3>
                        <ListingWrapper
                            isLoading={isLoading}
                            error={error as Error}
                            standardListProps={standardList}
                            footer={pagination()}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}

export default injectIntl(PaymentDetailsTransactionListing)


// WEBPACK FOOTER //
// ./src/payment/components/PaymentDetailsTransactionListing.tsx